import { Component, signal } from '@angular/core';
import { SetupWizardUnitForm } from '../../../../../forms/setup-wizard/setup-wizard-unit-form/setup-wizard-unit-form';
import { SetupWizardUnitsTable } from './setup-wizard-units-table/setup-wizard-units-table';
import { SetupWizard } from '../../../../../../services/setup-wizard';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
	SetupWizardLessonFormPayload,
	SetupWizardPayloadObjectKeys,
	SetupWizardUnit,
	SetupWizardUnitFormPayload,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SetupWizardUploadCsvButton } from '../../setup-wizard-upload-csv-button/setup-wizard-upload-csv-button';
import { SetupWizardDownloadCsvButton } from '../../setup-wizard-download-csv-button/setup-wizard-download-csv-button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { SetupWizardLessonForm } from '../../../../../forms/setup-wizard/setup-wizard-lesson-form/setup-wizard-lesson-form';

@Component({
	selector: 'app-setup-wizard-unit-editor',
	imports: [
		SetupWizardUnitForm,
		SetupWizardUnitsTable,
		NzButtonModule,
		SetupWizardUploadCsvButton,
		SetupWizardDownloadCsvButton,
		NzTabsModule,
		NzButtonModule,
		NzTooltipModule,
		SetupWizardLessonForm,
	],
	standalone: true,
	templateUrl: './setup-wizard-unit-editor.html',
	styleUrl: './setup-wizard-unit-editor.scss',
})
export class SetupWizardUnitEditor {
	public edit = signal<SetupWizardUnit | null>(null);
	public showForm = signal<boolean>(false);
	public loading = signal<boolean>(false);
	private clientUuid: string | undefined;
	private sub?: Subscription;
	public SetupWizardPayloadObjectKeys = SetupWizardPayloadObjectKeys;

	constructor(
		private route: ActivatedRoute,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	public onFormSubmit(payload: SetupWizardUnitFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.units, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onLessonFormSubmit(payload: SetupWizardLessonFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.lessons, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onFormClear() {
		this.showForm.set(false);
		this.edit.set(null);
	}

	public onAdd() {
		this.showForm.set(true);
		this.edit.set(null);
	}

	public onEdit($event: SetupWizardUnit) {
		if ($event.after || $event.before) {
			this.edit.set($event);
			this.showForm.set(true);
		}
	}

	public async onToggleDisable($event: SetupWizardUnit) {
		const beforeAfter = $event.after || $event.before;

		if (!beforeAfter) {
			throw new Error('No unit data available to update');
		}

		await this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.units, {
			...beforeAfter,
			active: !beforeAfter.active,
		});
	}

	public async onReset($event: SetupWizardUnit) {
		if (!$event.after) {
			throw new Error('No unit data available to reset');
		}

		await this.setupWizard.resetPayloadItem(SetupWizardPayloadObjectKeys.units, $event.after);
	}

	public async onRemove($event: SetupWizardUnit) {
		if (!$event.after) {
			throw new Error('No unit data available to remove');
		}

		await this.setupWizard.removePayloadItem(SetupWizardPayloadObjectKeys.units, $event.after);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
